<script setup lang="ts">
import {ref, computed, inject} from 'vue';
import {groupContainsPage, type SchematicHierarchyGroup, type SchematicHierarchyPage} from '../diff-list/schematic-hierarchy.ts';
import type {ProjectPageDiff} from '../../types/file_tree.ts';
import {SvgIcon} from '../../../svg.ts';
import {FILE_DIFF_STATUS} from '../../types/file_tree.ts';
import {statusClasses, statusIcon, shouldShowLeaf} from './utils.ts';
import type {Store} from '../../store.ts';

const store = inject<Store>('store');

const props = withDefaults(defineProps<{
  group: SchematicHierarchyGroup;
  childrenByPageId: Map<string, ProjectPageDiff>;
  forceExpand?: boolean;
  isRoot?: boolean;
}>(), {
  forceExpand: false,
  isRoot: true,
});

// Formats that carry instance identity on the group name need the row to tell
// repeated blocks apart; Altium and KiCad synthesize groups and want them gone.
const showHeader = computed((): boolean => !props.group.hideGroupNodes);

const collapsedPageIds = ref<Record<string, boolean>>({});
const isHeaderExpanded = ref(true);
const headerExpanded = computed(() => props.forceExpand || isHeaderExpanded.value);

function toggleHeader(): void {
  if (props.forceExpand) {
    return;
  }
  isHeaderExpanded.value = !isHeaderExpanded.value;
}

// A group with a single page and no child groups is redundant with its one
// sheet, so it collapses into a single row labelled with the block name.
const singlePage = computed((): SchematicHierarchyPage | null => {
  if (!showHeader.value) return null;
  if (props.group.unanchoredChildGroups.length > 0) return null;
  if (props.group.pages.length !== 1) return null;
  const page = props.group.pages[0];
  if (page.childGroups.length > 0) return null;
  return page;
});

function hasDiffPageInGroup(group: SchematicHierarchyGroup): boolean {
  return groupContainsPage(group, (id) => props.childrenByPageId.has(id));
}

function isPageExpanded(pageId: string): boolean {
  return props.forceExpand || !collapsedPageIds.value[pageId];
}

function togglePage(pageId: string): void {
  if (props.forceExpand) {
    return;
  }
  collapsedPageIds.value[pageId] = !collapsedPageIds.value[pageId];
}

type PageRow = {
  page: SchematicHierarchyPage;
  diffChild: ProjectPageDiff | null;
  hasChildGroups: boolean;
  hasChangedDescendants: boolean;
  visible: boolean;
  label: string | null;
};

function hasVisiblePageInGroup(group: SchematicHierarchyGroup): boolean {
  return groupContainsPage(group, (id) => {
    const child = props.childrenByPageId.get(id);
    return Boolean(child) && shouldShowLeaf(child.status, child.nameHash, store?.activeHash.value ?? '', store?.diffFilterConfig.showUnchangedSheets.value === true);
  });
}

const visible = computed((): boolean => hasVisiblePageInGroup(props.group));

const pageRows = computed((): PageRow[] => {
  // A single-page group with no child groups collapses into one row instead of a
  // header plus a redundant sheet row. A child group is a block instance, whose
  // name identifies the row better than the sheet's; a root group is the
  // schematic the sheet lives in, so there the sheet's own name is what counts.
  const collapseLabel = singlePage.value && !props.isRoot ? props.group.displayName : null;
  const result: PageRow[] = [];
  for (const page of props.group.pages) {
    const child = props.childrenByPageId.get(page.pageId);
    const hasChildGroups = page.childGroups.length > 0;
    const ownVisible = !child || shouldShowLeaf(child.status, child.nameHash, store?.activeHash.value ?? '', store?.diffFilterConfig.showUnchangedSheets.value === true);
    const rowVisible = ownVisible || (hasChildGroups && page.childGroups.some((g) => hasVisiblePageInGroup(g)));
    result.push({
      page,
      diffChild: child ?? null,
      hasChildGroups,
      hasChangedDescendants: page.childGroups.some((g) => hasDiffPageInGroup(g)),
      visible: rowVisible,
      label: collapseLabel,
    });
  }
  return result;
});

function rowShouldBeMuted(row: PageRow): boolean {
  if (row.diffChild?.nameHash === store?.activeHash.value) return false;
  if (row.hasChangedDescendants) return false;
  if (!row.diffChild) return true;
  return row.diffChild.isViewed || row.diffChild.status === FILE_DIFF_STATUS.UNCHANGED;
}

</script>

<template>
  <div v-if="!showHeader || visible">
    <div
      v-if="showHeader && !singlePage"
      class="tw:flex tw:items-center tw:gap-1 tw:p-1 tw:hover:cursor-pointer tw:hover:bg-hover"
      role="button"
      tabindex="0"
      :aria-expanded="headerExpanded"
      @click="toggleHeader"
      @keydown.enter.prevent="toggleHeader"
      @keydown.space.prevent="toggleHeader"
    >
      <div class="tw:w-4 tw:flex tw:justify-center tw:shrink-0">
        <SvgIcon :name="headerExpanded ? 'octicon-chevron-down' : 'octicon-chevron-right'" />
      </div>
      <span class="tw:text-ellipsis tw:overflow-hidden tw:whitespace-nowrap">{{ group.displayName }}</span>
    </div>
    <div
      v-show="!showHeader || singlePage || headerExpanded"
      :class="showHeader && !singlePage ? 'tw:ml-3 tw:border-l tw:pl-3 tw:border-secondary' : undefined"
    >
      <template
        v-for="row in pageRows"
        :key="row.page.pageId"
      >
        <div
          v-if="row.visible"
          class="tw:flex tw:items-center tw:gap-1 tw:p-1"
          :class="{
            'tw:hover:cursor-pointer tw:hover:bg-hover': row.hasChildGroups,
          }"
          @click="row.hasChildGroups && togglePage(row.page.pageId)"
        >
          <div class="tw:w-4 tw:flex tw:justify-center tw:shrink-0">
            <SvgIcon
              v-if="row.hasChildGroups"
              :name="isPageExpanded(row.page.pageId) ? 'octicon-chevron-down' : 'octicon-chevron-right'"
            />
          </div>
          <a
            v-if="row.diffChild"
            :title="row.diffChild.headPath"
            :href="`#diff-${row.diffChild.nameHash}`"
            class="tw:flex tw:items-center tw:gap-1 tw:text-text tw:no-underline tw:flex-grow tw:min-w-0"
            :class="{
              'tw:text-text tw:bg-active tw:rounded-[4px]': row.diffChild.nameHash === store?.activeHash.value,
              'tw:text-text-light-3': rowShouldBeMuted(row),
            }"
            @click.stop
          >
            <SvgIcon name="allspice-file-schematic" />
            <span class="tw:text-ellipsis tw:overflow-hidden tw:whitespace-nowrap">{{
              row.label ?? row.page.instanceLabel ?? row.diffChild.name ?? row.page.name
            }}</span>
            <SvgIcon
              v-if="row.diffChild.status !== FILE_DIFF_STATUS.UNCHANGED"
              :name="statusIcon(row.diffChild.status)"
              class="tw:ml-auto"
              :class="statusClasses(row.diffChild.status)"
            />
          </a>
          <div
            v-else
            class="tw:flex tw:items-center tw:gap-1 tw:flex-grow tw:min-w-0"
            :class="{'tw:text-text-light-3': rowShouldBeMuted(row)}"
          >
            <SvgIcon name="allspice-file-schematic" />
            <span class="tw:text-ellipsis tw:overflow-hidden tw:whitespace-nowrap">{{ row.label ?? row.page.instanceLabel ?? row.page.name }}</span>
          </div>
        </div>
        <div
          v-if="row.hasChildGroups"
          v-show="isPageExpanded(row.page.pageId)"
          class="tw:ml-3 tw:border-l tw:pl-3 tw:border-secondary"
        >
          <FileTreeHierarchyGroup
            v-for="childGroup in row.page.childGroups"
            :key="childGroup.groupId"
            :group="childGroup"
            :children-by-page-id="childrenByPageId"
            :force-expand="forceExpand"
            :is-root="false"
          />
        </div>
      </template>
      <FileTreeHierarchyGroup
        v-for="childGroup in group.unanchoredChildGroups"
        :key="childGroup.groupId"
        :group="childGroup"
        :children-by-page-id="childrenByPageId"
        :force-expand="forceExpand"
        :is-root="false"
      />
    </div>
  </div>
</template>
